import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useNavigate, Link } from 'react-router-dom';
import { LayoutDashboard, Package, PlusCircle, Boxes, Tags, Settings, LogOut, Menu, X, ExternalLink, Star, BadgePercent, Image, LayoutTemplate, Palette, ChevronsDown, Share2 } from 'lucide-react';
import { useAuth } from './AuthContext';
import { useLogo } from '../hooks/useLogo';

const MENU = [
  { to: '/admin', fim: true, icone: LayoutDashboard, nome: 'Visão geral', testid: 'admin-nav-dashboard' },
  { to: '/admin/produtos', icone: Package, nome: 'Produtos', testid: 'admin-nav-produtos' },
  { to: '/admin/produtos/novo', icone: PlusCircle, nome: 'Cadastrar produto', testid: 'admin-nav-novo' },
  { to: '/admin/estoque', icone: Boxes, nome: 'Controle de estoque', testid: 'admin-nav-estoque' },
  { to: '/admin/categorias', icone: Tags, nome: 'Categorias', testid: 'admin-nav-categorias' },
  { to: '/admin/destaques', icone: Star, nome: 'Produtos em destaque', testid: 'admin-nav-destaques' },
  { to: '/admin/promocoes', icone: BadgePercent, nome: 'Produtos em promoção', testid: 'admin-nav-promocoes' },
  { to: '/admin/banners', icone: Image, nome: 'Banners da página inicial', testid: 'admin-nav-banners' },
  { to: '/admin/personalizar', icone: LayoutTemplate, nome: 'Personalizar página inicial', testid: 'admin-nav-personalizar' },
  { to: '/admin/identidade', icone: Palette, nome: 'Logo e identidade visual', testid: 'admin-nav-identidade' },
  { to: '/admin/compartilhar', icone: Share2, nome: 'Compartilhar catálogo', testid: 'admin-nav-compartilhar' },
  { to: '/admin/configuracoes', icone: Settings, nome: 'Configurações da conta', testid: 'admin-nav-config' },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const logo = useLogo();
  const navigate = useNavigate();
  const [menuAberto, setMenuAberto] = useState(false);
  const sidebarRef = useRef(null);
  const [podeRolar, setPodeRolar] = useState(false);

  useEffect(() => {
    const el = sidebarRef.current;
    if (!el) return;
    const checar = () => setPodeRolar(el.scrollHeight > el.clientHeight && el.scrollTop + el.clientHeight < el.scrollHeight - 12);
    checar();
    el.addEventListener('scroll', checar);
    window.addEventListener('resize', checar);
    return () => {
      el.removeEventListener('scroll', checar);
      window.removeEventListener('resize', checar);
    };
  }, []);

  const sair = async () => {
    await logout();
    navigate('/admin/login', { replace: true });
  };

  const nav = (
    <nav className="flex flex-col gap-1">
      {MENU.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.fim}
          data-testid={item.testid}
          onClick={() => setMenuAberto(false)}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-bold transition-colors ${
              isActive ? 'bg-white/15 text-white' : 'text-blue-200/80 hover:bg-white/10 hover:text-white'
            }`
          }
        >
          <item.icone size={18} />
          {item.nome}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-slate-100 lg:flex">
      <aside ref={sidebarRef} className="hidden w-64 shrink-0 flex-col overflow-y-auto bg-mk-blue p-5 pb-16 lg:sticky lg:top-0 lg:flex lg:h-screen">
        <Link to="/admin" className="flex items-center gap-3 px-2 pb-6">
          <img src={logo} alt="MK" className="h-10 w-10 rounded-full object-contain" />
          <span>
            <span className="block font-heading text-sm font-extrabold text-white">MK Eletromóveis</span>
            <span className="block text-[10px] font-semibold uppercase tracking-widest text-blue-200/70">Painel Admin</span>
          </span>
        </Link>
        {nav}
        <div className="mt-auto space-y-1 pt-6">
          <Link to="/" className="flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-bold text-blue-200/80 transition-colors hover:bg-white/10 hover:text-white">
            <ExternalLink size={18} /> Ver catálogo
          </Link>
          <button
            data-testid="admin-logout-button"
            onClick={sair}
            className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-bold text-blue-200/80 transition-colors hover:bg-mk-red hover:text-white"
          >
            <LogOut size={18} /> Sair da conta
          </button>
        </div>
      </aside>

      {podeRolar && (
        <button
          data-testid="admin-sidebar-scroll-bottom"
          onClick={() => sidebarRef.current?.scrollTo({ top: sidebarRef.current.scrollHeight, behavior: 'smooth' })}
          aria-label="Rolar o menu até o final"
          className="fixed bottom-4 left-[7.5rem] z-30 hidden -translate-x-1/2 items-center gap-1.5 rounded-full bg-mk-red px-3.5 py-2 text-[11px] font-extrabold text-white shadow-lg shadow-mk-red/40 transition-transform hover:scale-105 lg:inline-flex"
        >
          <ChevronsDown size={14} className="animate-bounce" /> Mais opções
        </button>
      )}

      <div className="sticky top-0 z-30 flex items-center justify-between bg-mk-blue px-4 py-3 lg:hidden">
        <Link to="/admin" className="flex items-center gap-2.5">
          <img src={logo} alt="MK" className="h-9 w-9 rounded-full object-contain" />
          <span className="font-heading text-sm font-extrabold text-white">Painel Admin</span>
        </Link>
        <button
          data-testid="admin-menu-toggle"
          onClick={() => setMenuAberto(!menuAberto)}
          aria-label="Menu"
          className="rounded-lg p-2 text-white hover:bg-white/10"
        >
          {menuAberto ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {menuAberto && (
        <div className="fixed inset-0 z-40 bg-mk-bluedark/60 lg:hidden" onClick={() => setMenuAberto(false)}>
          <div className="h-full w-72 overflow-y-auto bg-mk-blue p-5" onClick={(e) => e.stopPropagation()}>
            {nav}
            <button
              data-testid="admin-logout-button-mobile"
              onClick={sair}
              className="mt-4 flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-bold text-blue-200/80 hover:bg-mk-red hover:text-white"
            >
              <LogOut size={18} /> Sair da conta
            </button>
          </div>
        </div>
      )}

      <main className="flex-1 p-4 sm:p-6 lg:p-8">
        <div className="mb-6 flex items-center justify-between">
          <p className="text-sm text-slate-500">
            Olá, <span className="font-bold text-slate-800">{user?.nome || 'Admin'}</span>
          </p>
          <span className="hidden text-xs text-slate-400 sm:block">{user?.email}</span>
        </div>
        <Outlet />
      </main>
    </div>
  );
}
